import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ethers } from 'ethers';
import { useWeb3 } from '../../hooks/useWeb3';
import { useContract } from '../../hooks/useContract';
import { useProjects } from '../../hooks/useProjects';
import { parseEther, formatEther } from '../../services/web3';
import { CONTRACT_ADDRESS, ESCROW_ABI } from '../../services/contracts/config';
import DashboardHeader from '../../components/common/DashboardHeader';
import BlockchainLoader from '../../components/common/BlockchainLoader';
import Toast from '../../components/common/Toast';

interface MilestoneInput {
  description: string;
  amount: string;
}

const CreateProjectPage: React.FC = () => {
  const navigate = useNavigate();
  const { account, signer } = useWeb3();
  const { createProject: createProjectAPI } = useProjects();
  const { createProject: createProjectContract } = useContract(signer);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockchainLoading, setBlockchainLoading] = useState(false);
  const [blockchainLoadingMessage, setBlockchainLoadingMessage] = useState<string>('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    provider: '',
    totalAmount: '',
  });

  const [milestones, setMilestones] = useState<MilestoneInput[]>([
    { description: '', amount: '' },
  ]);

  // Show message if no account - but don't redirect, let user connect wallet
  if (!account) {
    return (
      <div className="min-h-screen bg-white">
        <DashboardHeader />
        <div className="w-full max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-[#471b007f] font-dm hover:text-[#471b00] mb-4"
            >
              ← Back to My Projects
            </button>
            <h1 className="text-3xl font-semibold text-[#471b00] font-poly">
              Create New Project
            </h1>
          </div>
          <div className="text-center py-20 border border-[#471b001e] rounded-lg bg-gray-50">
            <p className="text-lg text-[#471b007f] font-dm mb-4">
              Please connect your MetaMask wallet to create a project
            </p>
            <p className="text-sm text-[#471b007f] font-dm mb-6">
              Use the "Connect MetaMask" button in the header to connect your wallet
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Back to My Projects
            </button>
          </div>
        </div>
      </div>
    );
  }

  const addMilestone = () => {
    setMilestones([...milestones, { description: '', amount: '' }]);
  };

  const removeMilestone = (index: number) => {
    if (milestones.length > 1) {
      setMilestones(milestones.filter((_, i) => i !== index));
    }
  };

  const updateMilestone = (index: number, field: keyof MilestoneInput, value: string) => {
    const updated = [...milestones];
    updated[index][field] = value;
    setMilestones(updated);
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!account || !signer) {
      setError('Please connect MetaMask wallet');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Validate form
      if (!formData.title || !formData.provider || !formData.totalAmount) {
        throw new Error('Please fill in all required fields');
      }

      if (milestones.some((m) => !m.description || !m.amount)) {
        throw new Error('Please fill in all milestone fields');
      }

      // Convert amounts to wei
      const totalAmount = parseEther(formData.totalAmount);
      const milestoneAmounts = milestones.map((m) => parseEther(m.amount));
      const milestoneDescriptions = milestones.map((m) => m.description);

      // Verify total
      const sum = milestoneAmounts.reduce((a, b) => a + b, BigInt(0));
      if (sum !== totalAmount) {
        throw new Error('Milestone amounts must sum to total amount');
      }

      // Show blockchain loader
      setBlockchainLoading(true);
      setBlockchainLoadingMessage('Creating project on blockchain...');

      // Create project on blockchain
      const tx = await createProjectContract(
        formData.provider,
        totalAmount,
        milestoneDescriptions,
        milestoneAmounts
      );

      setBlockchainLoadingMessage('Waiting for transaction confirmation...');
      // Wait for transaction to be mined
      const receipt = await tx.wait();
      
      // Get blockchain project ID from ProjectCreated event
      let blockchainProjectId: number | null = null;
      if (receipt && receipt.logs) {
        const contractInterface = new ethers.Interface(ESCROW_ABI);
        for (const log of receipt.logs) {
          try {
            const parsedLog = contractInterface.parseLog(log);
            if (parsedLog && parsedLog.name === 'ProjectCreated') {
              // ProjectCreated event: (projectId, client, provider, totalAmount)
              // projectId is at index 0
              const pid = parsedLog.args[0];
              blockchainProjectId = typeof pid === 'bigint' ? Number(pid) : Number(pid);
              console.log('✅ Extracted blockchain_project_id from ProjectCreated event:', blockchainProjectId);
              console.log('Full event args:', parsedLog.args);
              break;
            }
          } catch (e) {
            console.warn('Failed to parse log:', e);
            // Not a matching log, continue
          }
        }
      }

      if (blockchainProjectId === null) {
        console.error('❌ Failed to extract blockchain_project_id from ProjectCreated event');
        console.error('Receipt logs:', receipt?.logs);
        throw new Error('Failed to get project ID from blockchain. Please try again.');
      }

      setBlockchainLoadingMessage('Saving project to database...');
      
      // Create project in backend (will generate UUID automatically)
      // Convert amounts to wei strings for backend storage
      const createdProject = await createProjectAPI({
        title: formData.title,
        description: formData.description,
        client: account,
        provider: formData.provider,
        totalAmount: totalAmount.toString(), // Pass as wei string
        milestones: milestones.map((m) => ({
          amount: parseEther(m.amount).toString(), // Convert to wei string
          description: m.description,
        })),
        contractAddress: CONTRACT_ADDRESS || '',
        projectId: blockchainProjectId !== null ? blockchainProjectId : undefined,
      });

      // Hide blockchain loader
      setBlockchainLoading(false);
      setBlockchainLoadingMessage('');

      // Check if project already existed
      if ((createdProject as any).existing) {
        setToast({ message: 'Project already exists. Redirecting to existing project...', type: 'info' });
        // Navigate to existing project
        setTimeout(() => {
          navigate(`/dashboard/projects/${createdProject.id}`);
        }, 1500);
        return;
      }

      // Show success message
      setToast({ message: 'Project created successfully!', type: 'success' });

      // Redirect to project page
      setTimeout(() => {
        navigate(`/dashboard/projects/${createdProject.id}`);
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to create project');
      setToast({ message: err.message || 'Failed to create project', type: 'error' });
      console.error('Error creating project:', err);
    } finally {
      setLoading(false);
      setBlockchainLoading(false);
      setBlockchainLoadingMessage('');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <DashboardHeader />
      
      <div className="w-full max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-[#471b007f] font-dm hover:text-[#471b00] mb-4"
          >
            ← Back to My Projects
          </button>
          <h1 className="text-3xl font-semibold text-[#471b00] font-poly">
            Create New Project
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-[#471b00] font-dm mb-2">
              Project Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-[#471b001e] rounded-lg focus:outline-none focus:border-[#FF3333]"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#471b00] font-dm mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-[#471b001e] rounded-lg focus:outline-none focus:border-[#FF3333]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#471b00] font-dm mb-2">
              Provider Wallet Address *
            </label>
            <input
              type="text"
              value={formData.provider}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              placeholder="0x..."
              className="w-full px-4 py-2 border border-[#471b001e] rounded-lg focus:outline-none focus:border-[#FF3333]"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#471b00] font-dm mb-2">
              Total Amount (MATIC) *
            </label>
            <input
              type="number"
              step="0.0001"
              value={formData.totalAmount}
              onChange={(e) => setFormData({ ...formData, totalAmount: e.target.value })}
              className="w-full px-4 py-2 border border-[#471b001e] rounded-lg focus:outline-none focus:border-[#FF3333]"
              required
            />
          </div>

          <div>
            {/* Realtime milestone sum validation */}
            {(() => {
              const totalVal = parseFloat(formData.totalAmount) || 0;
              const milestoneSum = milestones.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);
              const diff = totalVal - milestoneSum;
              const hasMilestoneAmounts = milestones.some(m => parseFloat(m.amount) > 0);
              if (totalVal > 0 && hasMilestoneAmounts && Math.abs(diff) > 0.00001) {
                return (
                  <div className={`mb-4 p-3 rounded-lg border text-sm font-dm ${diff > 0 ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
                    {diff > 0
                      ? `Milestones sum: ${milestoneSum.toFixed(4)} MATIC. Remaining to allocate: ${diff.toFixed(4)} MATIC`
                      : `Milestones sum: ${milestoneSum.toFixed(4)} MATIC. Exceeds total by ${Math.abs(diff).toFixed(4)} MATIC`}
                  </div>
                );
              }
              if (totalVal > 0 && hasMilestoneAmounts && Math.abs(diff) <= 0.00001) {
                return (
                  <div className="mb-4 p-3 rounded-lg border bg-green-50 border-green-200 text-green-800 text-sm font-dm">
                    Milestones sum matches total amount
                  </div>
                );
              }
              return null;
            })()}

            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium text-[#471b00] font-dm">
                Milestones *
              </label>
              <button
                type="button"
                onClick={addMilestone}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                + Add Milestone
              </button>
            </div>

            <div className="space-y-4">
              {milestones.map((milestone, index) => (
                <div key={index} className="p-4 border border-[#471b001e] rounded-lg">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-medium text-[#471b00] font-dm">
                      Milestone {index + 1}
                    </span>
                    {milestones.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeMilestone(index)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-[#471b007f] font-dm mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={milestone.description}
                        onChange={(e) => updateMilestone(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-[#471b001e] rounded-lg focus:outline-none focus:border-[#FF3333] text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[#471b007f] font-dm mb-1">
                        Amount (MATIC)
                      </label>
                      <input
                        type="number"
                        step="0.0001"
                        value={milestone.amount}
                        onChange={(e) => updateMilestone(index, 'amount', e.target.value)}
                        className="w-full px-3 py-2 border border-[#471b001e] rounded-lg focus:outline-none focus:border-[#FF3333] text-sm"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 border border-[#471b001e] text-[#471b00] rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {blockchainLoading && (
        <BlockchainLoader
          message={blockchainLoadingMessage}
        />
      )}
    </div>
  );
};

export default CreateProjectPage;

